/**
 * Secrets Collective — keep fixed CTAs clear of Shopify's consent banner.
 *
 * The cookie banner (#shopify-pc__banner) is position: fixed at the bottom of
 * the viewport with z-index 2000000, and measures 356px tall on a phone. It
 * lands on top of the cart drawer's Check out button and the sticky add-to-bag
 * bar, so the click hits the banner's Decline button instead — checkout looks
 * broken until the shopper happens to dismiss the banner.
 *
 * Raising our UI above it is not an option: the consent controls have to stay
 * reachable. So measure the banner and publish how much room it steals:
 *
 *   --sc-consent-h        vertical overlap at the bottom of the viewport.
 *                         Used by the sticky add-to-bag bar, which is full width.
 *   --sc-consent-drawer-h the same, but only when the banner actually overlaps
 *                         the cart drawer horizontally. On desktop the drawer is
 *                         a 460px panel on the right and the banner sits to its
 *                         left, so they never collide and the drawer keeps its
 *                         full height. Shrinking it there would cost the item
 *                         list for nothing.
 *
 * The banner is injected asynchronously and removed on choice, so watch for
 * both, and re-measure on resize since its height depends on wrapping.
 */

const BANNER = '#shopify-pc__banner, .shopify-pc__banner';
const DRAWER = '.theme-drawer__dialog:has(.cart-drawer__inner)';

let observed = null;
let resizeObserver = null;

function publish(name, value) {
  document.documentElement.style.setProperty(name, `${Math.max(0, Math.round(value))}px`);
}

function visible(el) {
  if (!el || !el.isConnected) return false;
  const style = getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden' && style.position === 'fixed';
}

function measure() {
  const banner = document.querySelector(BANNER);

  if (!visible(banner)) {
    publish('--sc-consent-h', 0);
    publish('--sc-consent-drawer-h', 0);
    return banner && banner.isConnected ? banner : null;
  }

  const rect = banner.getBoundingClientRect();

  // Only the part that actually reaches the bottom of the viewport matters.
  const overlap = Math.max(0, window.innerHeight - rect.top);
  publish('--sc-consent-h', overlap);

  // The drawer only needs to give way if the two share horizontal space.
  const drawer = document.querySelector(DRAWER);
  let drawerOverlap = overlap;

  if (drawer) {
    const d = drawer.getBoundingClientRect();
    const shared = Math.min(rect.right, d.right) - Math.max(rect.left, d.left);
    if (shared <= 0) drawerOverlap = 0;
  }

  publish('--sc-consent-drawer-h', drawerOverlap);

  return banner;
}

function track() {
  const banner = measure();
  if (banner === observed) return;

  resizeObserver?.disconnect();
  observed = banner;

  if (banner && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => measure());
    resizeObserver.observe(banner);
  }
}

track();

/*
 * The banner arrives (and leaves) well after first paint, so watch for it.
 *
 * attributeFilter matters as much as childList here: a closed drawer is parked
 * off-screen to the right, so measuring the overlap while it is shut always
 * reports "no collision" and the offset sticks at zero for the rest of the
 * page. Re-measuring when [open] flips is what makes the drawer case correct.
 */
new MutationObserver(() => track()).observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['open'],
});

// Horizon's own open/close events, for drawers that animate into place — the
// attribute flips before the transition finishes moving the panel.
['theme-drawer:open', 'theme-drawer:close'].forEach((name) => {
  document.addEventListener(name, () => {
    measure();
    requestAnimationFrame(() => measure());
    setTimeout(() => measure(), 350);
  });
});

window.addEventListener('resize', () => measure(), { passive: true });
window.addEventListener('orientationchange', () => measure(), { passive: true });
